import { textToSend, transitionToSend } from './storedCrops';
import { LogoPosition } from './advancedScenes';
import { VideoScene, VideoTemplateStyle } from './types';
import { PreviewConfig, UpdateVideoSlideReorderRequest } from 'api/video';

/** SYM-6265. What belongs to the whole video rather than to a slide or a look. */
export interface VideoWideChoices {
  logoPosition?: LogoPosition;
}

export function buildUpdatePayload(
  draftId: number,
  scenes: VideoScene[],
  style: VideoTemplateStyle,
  previewConfig: PreviewConfig,
  choices: VideoWideChoices = {}
): UpdateVideoSlideReorderRequest {
  return {
    draft_id: draftId,
    config: {
      provider: previewConfig.provider,
      format: previewConfig.format,
      render_style: style,
      // SYM-6265. Only when the wizard was told a position to send: an older
      // caller that names none leaves the draft's own value alone.
      ...(choices.logoPosition ? { logo_position: choices.logoPosition } : {}),
      template_id: previewConfig.template_id,
      slides: scenes.map((s, position) => ({
        image: {
          index: s.index,
          ...(typeof s.resourceId === 'number'
            ? { resource_id: s.resourceId }
            : {}),
          // SYM-6272. Always named, null when there is none. The server
          // merges an update INTO the stored slide and keeps every key that
          // does not arrive, so leaving `crop` out after a picture swap kept
          // the old picture's crop on the new one (QA draft 25641: picture 0
          // -> 3, crop unchanged). A null that arrives does clear it.
          crop: s.imageCrop ?? null,
        },
        text: textToSend(scenes, position),
        subtext: s.subtext,
        duration: s.duration,
        // SYM-6215. Always sent, so a slide the customer never touched is
        // recorded as such and a reopened draft knows which lengths were
        // the customer's own.
        duration_set_by_hand: s.durationSetByHand === true,
        // SYM-6265. The same for the transition: the look decides it unless
        // the customer chose one under Advanced.
        transition_set_by_hand: s.transitionSetByHand === true,
        transition: transitionToSend(s) as typeof s.transition,
      })),
    },
  };
}

export function detectScenesEdited(
  scenes: VideoScene[],
  initialScenes: VideoScene[],
  style: VideoTemplateStyle,
  initialStyle: VideoTemplateStyle,
  logoPosition?: LogoPosition,
  initialLogoPosition?: LogoPosition
): boolean {
  if (scenes.length !== initialScenes.length) return true;
  if (style !== initialStyle) return true;
  if ((logoPosition ?? null) !== (initialLogoPosition ?? null)) return true;

  return scenes.some((s, i) => {
    const orig = initialScenes[i];
    return (
      s.index !== orig?.index ||
      s.headline !== orig?.headline ||
      s.subtext !== orig?.subtext ||
      s.imageUrl !== orig?.imageUrl ||
      s.resourceId !== orig?.resourceId ||
      s.duration !== orig?.duration ||
      // What would be SENT, not what is shown: slide-up and slide look alike.
      transitionToSend(s) !== (orig ? transitionToSend(orig) : undefined) ||
      (s.durationSetByHand === true) !== (orig?.durationSetByHand === true) ||
      (s.transitionSetByHand === true) !==
        (orig?.transitionSetByHand === true) ||
      JSON.stringify(s.imageCrop) !== JSON.stringify(orig?.imageCrop)
    );
  });
}
